
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { createErrorToast } from '@/components/ui/enhanced-toast';

interface AdminUser {
  id: string;
  email: string;
  created_at: string;
  email_confirmed_at: string | null;
  last_sign_in_at: string | null;
  banned_until: string | null;
  profile: {
    display_name: string | null;
    avatar_url: string | null;
  } | null;
  role: string;
}

export const useAdminUsers = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const errorToast = createErrorToast(toast);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      console.log('[ADMIN] Fetching users...');
      const { data, error } = await supabase.functions.invoke('admin-users', {
        body: { action: 'list-users' }
      });

      console.log('[ADMIN] Fetch users response:', { data, error });

      if (error) throw error;

      setUsers(data.users || []);
      console.log('[ADMIN] Users loaded successfully:', data.users?.length || 0);
    } catch (error: any) {
      console.error('[ADMIN] Error fetching users:', error);
      errorToast(
        'Fout bij laden gebruikers',
        `${error.message || 'Er ging iets mis bij het laden van gebruikers.'} Controleer de console voor details.`,
        error,
        { action: 'fetchUsers', path: window.location.pathname }
      );
    }
    setLoading(false);
  };

  const inviteUser = async (email: string, role: 'admin' | 'super_admin' = 'admin') => {
    try {
      console.log('[ADMIN] Inviting user:', { email, role });
      const { data, error } = await supabase.functions.invoke('admin-users', {
        body: { 
          action: 'invite-user',
          email,
          role
        }
      });

      console.log('[ADMIN] Invite user response:', { data, error });

      if (error) throw error;

      toast({
        title: 'Gebruiker uitgenodigd',
        description: `Een uitnodiging is verstuurd naar ${email}. Controleer de edge function logs voor details.`,
      });

      await fetchUsers();
      return true;
    } catch (error: any) {
      console.error('[ADMIN] Error inviting user:', error);
      
      // Show user-friendly error message based on error type
      if (error.message?.includes('503') || error.message?.includes('fetch')) {
        errorToast(
          "Verbindingsfout",
          "De gebruikersbeheer service is tijdelijk niet beschikbaar. Probeer het later opnieuw.",
          error,
          { action: 'inviteUser', email, role }
        );
      } else {
        errorToast(
          "Fout bij uitnodigen",
          error.message || "Er is een fout opgetreden bij het uitnodigen van de gebruiker.",
          error,
          { action: 'inviteUser', email, role }
        );
      }
      
      return false;
    }
  };

  const updateUserRole = async (userId: string, role: 'admin' | 'super_admin') => {
    try {
      const { error } = await supabase.functions.invoke('admin-users', {
        body: { 
          action: 'update-role',
          userId,
          role
        }
      });

      if (error) throw error;

      toast({
        title: 'Rol bijgewerkt',
        description: 'De gebruikersrol is succesvol bijgewerkt.',
      });

      await fetchUsers();
      return true;
    } catch (error: any) {
      console.error('Error updating user role:', error);
      toast({
        title: 'Fout bij bijwerken rol',
        description: error.message || 'Er ging iets mis bij het bijwerken van de rol.',
        variant: 'destructive'
      });
      return false;
    }
  };

  const resetUserPassword = async (email: string) => {
    try {
      const { error } = await supabase.functions.invoke('admin-users', {
        body: { 
          action: 'reset-password',
          email
        }
      });

      if (error) throw error;

      toast({
        title: 'Wachtwoord reset verstuurd',
        description: `Een wachtwoord reset link is verstuurd naar ${email}.`,
      });

      return true;
    } catch (error: any) {
      console.error('Error resetting password:', error);
      toast({
        title: 'Fout bij wachtwoord reset',
        description: error.message || 'Er ging iets mis bij het versturen van de wachtwoord reset.',
        variant: 'destructive'
      });
      return false;
    }
  };

  const setUserPassword = async (userId: string, password: string) => {
    try {
      const { error } = await supabase.functions.invoke('admin-users', {
        body: {
          action: 'set-password',
          userId,
          password
        }
      });

      if (error) throw error;

      toast({
        title: 'Wachtwoord ingesteld',
        description: 'Het wachtwoord is succesvol gewijzigd.'
      });

      return true;
    } catch (error: any) {
      console.error('Error setting user password:', error);
      toast({
        title: 'Fout bij wachtwoord instellen',
        description: error.message || 'Er ging iets mis bij het instellen van het wachtwoord.',
        variant: 'destructive'
      });
      return false;
    }
  };
  const deleteUser = async (userId: string) => {
    try {
      const { error } = await supabase.functions.invoke('admin-users', {
        body: { 
          action: 'delete-user',
          userId
        }
      });

      if (error) throw error;

      toast({
        title: 'Gebruiker verwijderd',
        description: 'De gebruiker is succesvol verwijderd.',
      });

      await fetchUsers();
      return true;
    } catch (error: any) {
      console.error('Error deleting user:', error);
      toast({
        title: 'Fout bij verwijderen',
        description: error.message || 'Er ging iets mis bij het verwijderen van de gebruiker.',
        variant: 'destructive'
      });
      return false;
    }
  };

  const impersonateUser = async (userId: string): Promise<string | null> => {
    try {
      console.log('[ADMIN] Impersonating user:', userId);
      const { data, error } = await supabase.functions.invoke('admin-users', {
        body: { 
          action: 'impersonate-user',
          userId
        }
      });

      console.log('[ADMIN] Impersonation response:', { data, error });

      if (error) throw error;

      if (data.error) {
        throw new Error(data.error);
      }

      toast({
        title: 'Impersonation link gegenereerd',
        description: `Opent nieuw tabblad als ${data.userEmail}`,
      });

      return data.loginUrl;
    } catch (error: any) {
      console.error('[ADMIN] Error impersonating user:', error);
      toast({
        title: 'Impersonation mislukt',
        description: error.message || 'Er ging iets mis bij het genereren van de login link.',
        variant: 'destructive'
      });
      return null;
    }
  };

  return {
    users,
    loading,
    fetchUsers,
    inviteUser,
    updateUserRole,
    resetUserPassword,
    setUserPassword,
    deleteUser,
    impersonateUser
  };
};
